import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { getRenterQualificationByTenantScreeningCriteria } from 'common/householdApplication/prequalificationHelpers';
import { getListingFullAddressString } from 'common/listingHelpers';

import { RENTAL_APPLICATION_STEP_PERSONAL_INFO } from 'common/householdApplication/rentalApplicationHelpers';
import { SR_API_ACTION_HOUSEHOLD_APPLICATION_MARK_RENTER_REQUIREMENTS } from 'api/actionTypes';

import SrLink from 'client/components/SrLink/SrLink';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrTextList from 'client/components/SrTextList';
import SrMarkdown from 'client/components/SrMarkdown';
import SrSeparator from 'client/components/SrSeparator';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrRentalApplicationFlowStepHeader from 'client/components/SrRentalApplicationFlowStepHeader';

import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import withApiState from 'client/helpers/withApiState';
import HouseholdApplicationActions from 'client/actions/HouseholdApplication';
import HouseholdApplicationSubmissionActions from 'client/actions/HouseholdApplicationSubmission';
import SrApplicationProcessVideo from 'client/components/SrApplicationProcessVideo';

@withApiState({
  markRenterRequirementsApiState: {
    action: SR_API_ACTION_HOUSEHOLD_APPLICATION_MARK_RENTER_REQUIREMENTS,
  },
})
@withInfiniteScroll()
class SrRentalApplicationFlowStepAgreement extends Component {
  static propTypes = {
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    markRenterRequirementsApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    listing: PropTypes.object.isRequired,
    renterProfile: PropTypes.object.isRequired,
    tenantScreeningCriteria: PropTypes.object.isRequired,
    householdApplicationId: PropTypes.number,
    guarantorApplicationId: PropTypes.number,
    householdApplicationSubmissionId: PropTypes.number,
    goToNextRentalApplicationStep: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._onSrollToTheBottom);

    this.state = {
      hasScrollToTheBottom: false,
    };
  }

  _onSrollToTheBottom = () => {
    this.setState({
      hasScrollToTheBottom: true,
    });
  };

  _onAgreeRenterRequirements = () => {
    const {
      dispatch,
      goToNextRentalApplicationStep,
      householdApplicationId,
      householdApplicationSubmissionId,
      listing,
    } = this.props;

    if (householdApplicationId) {
      dispatch(HouseholdApplicationActions.markRenterRequirements, {
        applicationId: householdApplicationId,
        listingId: listing.id,
      }).then(() => {
        goToNextRentalApplicationStep(RENTAL_APPLICATION_STEP_PERSONAL_INFO);
      });
    } else if (householdApplicationSubmissionId) {
      dispatch(HouseholdApplicationSubmissionActions.markRenterRequirements, {
        applicationSubmissionId: householdApplicationSubmissionId,
      }).then(() => {
        goToNextRentalApplicationStep(RENTAL_APPLICATION_STEP_PERSONAL_INFO);
      });
    }
  };

  _getMainRequirementsList = () => {
    const { listing, renterProfile, tenantScreeningCriteria } = this.props;

    const { requirements } = getRenterQualificationByTenantScreeningCriteria({
      listing,
      tenantScreeningCriteria,
      householdApplication: renterProfile,
    });

    const titlesToExclude = ['Dog Breed'];

    const filteredRequirements = requirements.filter(({ title }) => {
      return !titlesToExclude.includes(title);
    });

    return [
      ...filteredRequirements.map(({ title }) => title),
      'Seen the home in-person',
      'Not all felony types are accepted',
    ];
  };

  _getRestrictedDogBreeds = () => {
    const { tenantScreeningCriteria } = this.props;

    if (tenantScreeningCriteria.breedRestrictions?.length === 0) {
      return [];
    }

    return tenantScreeningCriteria.breedRestrictions.map(({ name }) => name).join(', ');
  };

  render() {
    const {
      onInfiniteScrollWrapperRef,
      markRenterRequirementsApiState,
      listing,
      tenantScreeningCriteria,
      householdApplicationId,
      guarantorApplicationId,
    } = this.props;

    const { hasScrollToTheBottom } = this.state;

    const mainRequirements = this._getMainRequirementsList();
    const restrictedDogBreeds = this._getRestrictedDogBreeds();

    return (
      <div className="RentalApplicationPage">
        <SrRentalApplicationFlowStepHeader
          isGuarantorApp={guarantorApplicationId && !householdApplicationId}
          activeStep={0}
        />
        <div className="main">
          <div className="mainContent">
            <SrText className="pageHeadline" size="2xl" weight="medium" leading="2xl">
              Renter Requirements for
              <SrText size="2xl" weight="bold" leading="2xl">
                {` ${getListingFullAddressString(listing)}`}
              </SrText>
              <SrBox marginTop="13px" width="100%">
                <SrText display="block" fontSize="13px" weight="500">
                  (You must scroll to the very bottom to continue)
                </SrText>
              </SrBox>
            </SrText>
            <SrSeparator />
            <SrText weight="bold" display="block">
              Main Requirements
            </SrText>
            <SrTextList textList={mainRequirements} />
            <SrSeparator />
            <SrText weight="bold" display="block" align="center">
              Rent Your Home in 6 Minutes:
              <SrLink to="https://youtu.be/PHTjb4XwNtw?si=yAULpHzrFfTjKwve">
                {' Watch Video'}
              </SrLink>{' '}
              📺
            </SrText>
            <SrBox margin="10px 0">
              <SrApplicationProcessVideo />
            </SrBox>
            <SrSeparator />
            {restrictedDogBreeds.length > 0 && (
              <>
                <SrBox marginBottom="12px">
                  <SrText weight="bold" display="block" paddingBottom="5px">
                    Restricted Dog Breeds
                  </SrText>
                  <SrText>{restrictedDogBreeds}</SrText>
                </SrBox>
                <SrSeparator />
              </>
            )}
            <SrMarkdown
              onRef={onInfiniteScrollWrapperRef}
              source={tenantScreeningCriteria.extraApplicantRequirements}
            />
            <SrStaticFooter size="135">
              <ButtonDeprecated
                disabled={!hasScrollToTheBottom || markRenterRequirementsApiState.inProgress}
                onClick={this._onAgreeRenterRequirements}
              >
                i understand & agree
              </ButtonDeprecated>
            </SrStaticFooter>
          </div>
        </div>
      </div>
    );
  }
}

SrRentalApplicationFlowStepAgreement.displayName = 'SrRentalApplicationFlowStepAgreement';

export default SrRentalApplicationFlowStepAgreement;
